import { AButton, AInput, ASelect, ATag } from 'components/Antd';
import { Modal, List, Input, Space, Divider, Row, Col } from 'antd';
import React from 'react';
import { StaticDataContext } from 'context/StaticDataProvider/index';
import { SearchOutlined, CloseCircleFilled } from '@ant-design/icons';
import styled, { css } from 'styled-components';
import sum from 'lodash/sum';
import cloneDeep from 'lodash/cloneDeep';
import T from 'i18n-react';

const SelectInner = styled(ASelect)`
  min-width: 140px;
  > .ant-select-selector {
    border: none !important;
    box-shadow: none !important;
  }
`;

const StyledSelectedTag = styled(ATag).withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  display: flex;
  align-items: center;
  padding: 4px 6px 4px 8px;
  margin-bottom: 10px;
  border-radius: 13px;
  background: var(--Text-text-400, #4e5a71);

  color: var(--Neutrals-white, #fff);
  /* Body/Body 3 - Regular */
  font-family: Roboto;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 16px; /* 133.333% */
  letter-spacing: 0.06px;
  .ant-tag-close-icon {
    font-size: 14px;
    color: var(--Neutrals-white, #fff);
  }
`;

const StyledTag = styled(ATag).withConfig({
  shouldForwardProp: (prop) => prop !== 'selected',
})`
  padding: 7px 12px;
  border-radius: 50px;
  border: 1px solid var(--Text-text-100, #ebedef);
  background: var(--Neutrals-grey-25, #fafafa);

  /* Body/Body 2 - Regular */
  font-family: Roboto;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 18px; /* 128.571% */
  letter-spacing: 0.028px;
  ${(p) =>
    p.selected &&
    css`
      background: var(--Text-text-400, #4e5a71);
      color: var(--Neutrals-white, #fff);
    `}
`;

const StyledMeta = styled(List.Item.Meta)`
  .ant-list-item-meta-title {
    color: var(--Text-text-600, #232b3a);

    /* Body/Body 3 - Medium */
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 500;
    line-height: 14px; /* 116.667% */
    letter-spacing: 0.06px;
    margin-bottom: 12px;
  }
`;

const StyledList = styled(List)`
  .ant-list-header {
    border-bottom: 0;
  }
  ul {
    max-height: 50vh;
    overflow: hidden;
    overflow-y: scroll;
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-footer {
    text-align: left;
    span.caption {
      color: #000;

      /* Body/Body 3 - Regular */
      font-family: Roboto;
      font-size: 12px;
      font-style: normal;
      font-weight: 400;
      line-height: 16px; /* 133.333% */
      letter-spacing: 0.06px;
    }
  }
`;

const CategorySelectedItem = styled(List.Item)`
  .ant-list-item-meta-title {
    color: var(--Text-text-600, #232b3a);

    /* Body/Body 3 - Regular */
    font-family: Roboto;
    font-size: 12px;
    font-style: normal;
    font-weight: 400;
    line-height: 16px; /* 133.333% */
    letter-spacing: 0.06px;
  }
  .ant-list-item-meta-description {
    color: var(--Text-text-300, #8991a0);

    /* Body/Caption 1 - Regular */
    font-family: Roboto;
    font-size: 10px;
    font-style: normal;
    font-weight: 400;
    line-height: 12px; /* 120% */
    letter-spacing: 0.1px;
  }
`;

const WrappedListSelected = styled.div`
  display: flex;
  width: 100%;
  align-items: center;
  border-radius: 4px;
  border: 1px solid var(--Text-text-100, #ebedef);
  padding: 12px;
  > div:first-child {
    flex: 1;
    > li {
      padding: 8px 0;
    }
  }
`;

const SelectSubCategory = ({ value, onChange, disabled }) => {
  const [filter, setFilter] = React.useState({ value: 'all' });
  const [keyword, setKeyword] = React.useState('');
  const [selected, setSelected] = React.useState(new Map([]));
  const { getOptions } = React.useContext(StaticDataContext);
  const categoriesOptions = (getOptions('productCategories') || []).filter(
    ({ productSubCategories }) => !!(productSubCategories || []).length
  );

  const handleChange = (newSelected) => {
    const next = Array.from(newSelected).reduce((acc, current) => {
      const [categoryId, subCategory] = current;
      const category = categoriesOptions.find(({ id, value }) => (id || value) === categoryId);
      acc.push({
        ...category,
        subCategories: subCategory,
      });
      return acc;
    }, []);
    onChange(next);
  };

  const handleUnSelect = (subCategory) => () => {
    const categoryId = subCategory.productCategoryId;
    const subCategories = selected.get(categoryId) || [];
    const index = subCategories.findIndex(({ id }) => subCategory.id === id);
    if (index > -1) {
      subCategories.splice(index, 1);
    }
    if (subCategories.length) {
      selected.set(categoryId, subCategories);
    } else {
      selected.delete(categoryId);
    }
    setSelected(new Map(selected));
    handleChange(selected);
  };

  const handleSelect = (subCategory) => () => {
    const categoryId = subCategory.productCategoryId;
    const categories = selected.get(categoryId) || [];
    const index = categories.findIndex(({ id }) => subCategory.id === id);
    if (index > -1) {
      handleUnSelect(subCategory)();
      return;
    }
    categories.push(subCategory);
    selected.set(categoryId, categories);
    setSelected(new Map(selected));
    handleChange(selected);
  };

  const selectedSubCategories = React.useMemo(() => {
    return Array.from(selected).reduce((acc, [, subCategory]) => {
      return [...acc, ...subCategory];
    }, []);
  }, [selected]);

  const categories = React.useMemo(() => {
    return categoriesOptions
      .filter(({ id }) => {
        if (filter.value === 'all') return true;
        return filter.value === id;
      })
      .filter(({ productSubCategories }) => {
        const found = productSubCategories.filter(({ name_en, name_cn }) => {
          return (
            name_en?.toLowerCase().includes(keyword.toLowerCase()) ||
            name_cn?.toLowerCase().includes(keyword.toLowerCase())
          );
        });

        return keyword ? !!found.length : true;
      });
  }, [categoriesOptions, keyword, filter]);

  React.useEffect(() => {
    if (value !== undefined) {
      if (value === null) {
        setSelected(new Map([]));
        return;
      }
      setSelected(
        new Map(
          value.map((category) => {
            return [category.id, category.subCategories];
          })
        )
      );
    }
  }, [value]);

  return (
    <StyledList
      dataSource={[...categories]}
      renderItem={(category) => {
        return (
          <List.Item>
            <StyledMeta
              title={category.label}
              description={
                <Row gutter={[8, 8]}>
                  {category.productSubCategories
                    .filter((subCategory) => {
                      const found =
                        subCategory.name_en?.toLowerCase().includes(keyword.toLowerCase()) ||
                        subCategory.name_cn?.toLowerCase().includes(keyword.toLowerCase());
                      return keyword ? found : true;
                    })
                    .map((subCategory) => {
                      const isSelected = selectedSubCategories.findIndex(
                        ({ id }) => id === subCategory.id
                      );
                      return (
                        <Col key={subCategory.id}>
                          <StyledTag onClick={handleSelect(subCategory)} selected={isSelected > -1}>
                            {subCategory.name_en}
                          </StyledTag>
                        </Col>
                      );
                    })}
                </Row>
              }
            />
          </List.Item>
        );
      }}
      header={
        <React.Fragment>
          <Input.Group>
            <AInput
              onChange={(e) => {
                setKeyword(e.currentTarget.value);
              }}
              prefix={<SearchOutlined />}
              suffix={
                <Space>
                  <Divider type="vertical" style={{ borderColor: '#EBEDEF', height: '1.25em' }} />
                  <SelectInner
                    value={{ value: 'all' }}
                    onChange={(_, option) => {
                      setFilter(option);
                    }}
                    options={[
                      {
                        value: 'all',
                        label: T.translate('general.all', {
                          suffix: T.translate('general.categories'),
                        }),
                      },
                      ...categoriesOptions,
                    ]}
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                  />
                </Space>
              }
            />
          </Input.Group>

          {!!selectedSubCategories.length && (
            <React.Fragment>
              <Divider />
              <Row>
                {selectedSubCategories.map((subCategory) => {
                  return (
                    <Col key={subCategory.id}>
                      <StyledSelectedTag
                        closable
                        onClose={handleUnSelect(subCategory)}
                        closeIcon={<CloseCircleFilled />}>
                        {subCategory.name_en}
                      </StyledSelectedTag>
                    </Col>
                  );
                })}
              </Row>
            </React.Fragment>
          )}
        </React.Fragment>
      }
    />
  );
};

/**
 * value :[{
 * ...category,
 * subCategories:[...selectedSubCategory]
 * }]
 */
const SelectCategoryWithModal = ({ value, onChange, disabled }) => {
  const [visible, setVisible] = React.useState(false);
  const [selected, setSelected] = React.useState([]);

  const toggleVisible = () => {
    setVisible(!visible);
  };

  const selectedCount = React.useMemo(() => {
    return sum(selected.map(({ subCategories = [] }) => subCategories.length));
  }, [selected]);

  const handleChange = (selectedCategory) => {
    setSelected(selectedCategory);
  };

  React.useEffect(() => {
    setSelected(cloneDeep(value || []));
  }, [value]);

  return (
    <React.Fragment>
      <div>
        {Array.isArray(value) && value.length ? (
          <WrappedListSelected className="wrapped-selected-category">
            <List
              split={false}
              dataSource={selected}
              locale={{ emptyText: '' }}
              renderItem={(category) => {
                return (
                  <CategorySelectedItem>
                    <List.Item.Meta
                      title={category.title || category.label}
                      description={category.subCategories
                        .map(({ name_en }) => name_en)
                        .join(', ')}></List.Item.Meta>
                  </CategorySelectedItem>
                );
              }}></List>
            <AButton type="primary" ghost onClick={toggleVisible} disabled={disabled}>
              {T.translate('general.edit', { suffix: T.translate('general.category') })}
            </AButton>
          </WrappedListSelected>
        ) : (
          <AButton type="primary" ghost onClick={toggleVisible} disabled={disabled}>
            {T.translate('general.select', { suffix: T.translate('general.category') })}
          </AButton>
        )}
      </div>
      <StyledModal
        onCancel={toggleVisible}
        width="70%"
        title={T.translate('general.select', { suffix: T.translate('general.category') })}
        visible={visible}
        footer={
          <Space>
            <AButton
              type="primary"
              onClick={() => {
                onChange(selected);
                toggleVisible();
              }}>
              {T.translate('general.add', { suffix: T.translate('general.category') })}
            </AButton>
            {!!selectedCount && (
              <span className="caption">
                {selectedCount} {T.translate('general.selected')}
              </span>
            )}
          </Space>
        }>
        <SelectSubCategory onChange={handleChange} value={selected} />
      </StyledModal>
    </React.Fragment>
  );
};

export default SelectCategoryWithModal;
